<script>

import { angularInvoke } from '/vodalys/vvuejs'

export default {
    translate25Context: "transcript",
    props: ["component", "handler"],
    data: function () {
        return {
            notificationKeys: {},
            vnotification_service_js: null,
            showModal: false,
            showAdd: false,
            selectedLang: null,
            langToDelete: null,
            replaceLang: null,
            uploadingLangId: null,
            dragLangId: null,
            pending: false,
        };
    },
    mounted: function () {
        this.vnotification_service_js = angularInvoke(
            (vnotification_service_js) => vnotification_service_js
        );
    },
    destroyed() {
        if (this.vnotification_service_js) {
            Object.keys(this.notificationKeys).forEach((key) => {
                this.vnotification_service_js.remove(key);
            });
        }
    },
    computed: {
        isLoading() {
            return ['init', 'running'].includes(this.component.task_state);
        },
        langs() {
            return Object.values(this.component.langs || {});
        },
        currentLangs() {
            return this.langs.map((lang) => lang.value);
        },
        langOptions() {
            return (this.component.available_langs || [])
                .filter((code) => !this.currentLangs.includes(code))
                .map((code) => ({ key: code, title: this.$vt(code) }))
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((option, index) => ({ ...option, weight: index }));
        },
    },
    watch: {
        selectedLang(lang) {
            if (lang) {
                this.addLang();
            }
        },
        showModal(show) {
            if (!show) {
                this.showAdd = false;
                this.langToDelete = null;
            }
        },
    },
    methods: {
        notify(label, severity) {
            const key = this.vnotification_service_js.add({
                '#type': 'vnotification:js',
                'label': label,
                'delay': 6 * 1000,
                'severity': severity,
            });
            this.notificationKeys[key] = key;
        },
        isLang(lang, id) {
            return !!id && lang['#id'] === id;
        },
        exportUrl(lang) {
            return lang.actions?.export_vtt?.url;
        },
        importUrl(lang) {
            return lang.actions?.import_vtt?.url;
        },
        async createSession() {
            if (this.pending) {
                return;
            }
            this.pending = true;
            try {
                await this.handler.action(this.component, 'create_session', []);
            } catch (error) {
                this.notify(this.$vt('Error adding subtitles'), 1);
            } finally {
                this.pending = false;
            }
        },
        async addLang() {
            if (!this.selectedLang || this.pending) {
                return;
            }
            this.pending = true;
            try {
                await this.handler.action(this.component, 'add_lang', [this.selectedLang.key]);
                this.showAdd = false;
            } catch (error) {
                this.notify(this.$vt('Error adding language'), 1);
            } finally {
                this.selectedLang = null;
                this.pending = false;
            }
        },
        openAdd() {
            this.showAdd = true;
            // Deferred so that the current click is not caught by the select's click-outside handler
            setTimeout(() => this.$refs.addSelect && this.$refs.addSelect.open());
        },
        cancelAdd() {
            this.showAdd = false;
            this.selectedLang = null;
        },
        async confirmRemoveLang() {
            if (!this.langToDelete || this.pending) {
                return;
            }
            this.pending = true;
            try {
                await this.handler.action(this.component, 'remove_lang', [this.langToDelete.value]);
            } catch (error) {
                this.notify(this.$vt('Error deleting language'), 1);
            } finally {
                this.pending = false;
                this.langToDelete = null;
            }
        },
        openReplace(lang) {
            this.replaceLang = lang;
            this.$refs.file.value = '';
            this.$refs.file.click();
        },
        onFileChange(event) {
            const file = event.target.files && event.target.files[0];
            if (file && this.replaceLang) {
                this.upload(this.replaceLang, file);
            }
            this.replaceLang = null;
        },
        onDragOver(lang) {
            if (!this.uploadingLangId && !this.langToDelete) {
                this.dragLangId = lang['#id'];
            }
        },
        onDragLeave(lang, event) {
            if (this.isLang(lang, this.dragLangId) && !event.currentTarget.contains(event.relatedTarget)) {
                this.dragLangId = null;
            }
        },
        onDrop(lang, event) {
            const isTarget = this.isLang(lang, this.dragLangId);
            this.dragLangId = null;
            const file = event.dataTransfer && event.dataTransfer.files[0];
            if (isTarget && file) {
                this.upload(lang, file);
            }
        },
        async upload(lang, file) {
            if (this.uploadingLangId) {
                return;
            }
            if (!file.name.toLowerCase().endsWith('.vtt')) {
                this.notify(this.$vt('Please select a .vtt file'), 1);
                return;
            }
            const url = this.importUrl(lang);
            if (!url) {
                return;
            }

            this.uploadingLangId = lang['#id'];
            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await fetch(url, { method: 'POST', body: formData });
                const responseText = await response.text();

                let result = null;
                try {
                    result = JSON.parse(responseText);
                } catch (e) {
                    result = { error: responseText };
                }

                if (!response.ok || result.error) {
                    throw new Error(result.error || 'Upload failed');
                }

                this.notify(this.$vt('Subtitles imported ({0})', [result.count]), 0);
            } catch (error) {
                this.notify(this.$vt('Error uploading file: {0}', [error.message]), 1);
            } finally {
                this.uploadingLangId = null;
            }
        },
    },
};
</script>

<template>
    <div class="transcript-vvod">

        <input type="file" ref="file" accept=".vtt" class="transcript-vvod-file" @change="onFileChange($event)" />

        <f-modal-base backdrop-close v-if="showModal" closable :title2="$vt('Subtitles')" @close="showModal = false">

            <template #content>

                <div class="transcript-vvod-state" v-if="isLoading">
                    <f-loader-circular></f-loader-circular>
                    <span>{{ $vt('Loading subtitles...') }}</span>
                </div>

                <div class="transcript-vvod-state" v-else-if="!component.has_subtitles">
                    <f-icon class="transcript-vvod-state-icon" icon="fa6-closed-captioning"></f-icon>
                    <span>{{ $vt('No subtitles for this file') }}</span>
                    <f-button v-if="component.can_create_session" type="secondary" :pending="pending" @click="createSession()">
                        <f-icon icon="fa6-plus"></f-icon>{{ $vt('Add subtitles') }}
                    </f-button>
                </div>

                <template v-else>

                    <div class="transcript-vvod-langs" @dragover.prevent @drop.prevent>

                        <div class="transcript-vvod-lang" v-for="lang in langs" :key="lang['#id']"
                            :class="{ confirm: isLang(lang, langToDelete && langToDelete['#id']), dragover: isLang(lang, dragLangId) }"
                            @dragover.prevent="onDragOver(lang)" @dragleave="onDragLeave(lang, $event)"
                            @drop.prevent="onDrop(lang, $event)">

                            <template v-if="isLang(lang, langToDelete && langToDelete['#id'])">
                                <span class="transcript-vvod-confirm">
                                    <f-icon icon="fa6-triangle-exclamation"></f-icon>
                                    {{ $vt('Delete subtitles for this language?') }}
                                    <strong>{{ $vt(lang.label) }}</strong>
                                </span>
                                <span class="transcript-vvod-actions">
                                    <f-button type="tertiary" :disabled="pending" @click="langToDelete = null">{{ $vt('Cancel') }}</f-button>
                                    <f-button type="negative small" :pending="pending" @click="confirmRemoveLang()">
                                        <f-icon icon="fa6-trash"></f-icon>{{ $vt('Delete') }}
                                    </f-button>
                                </span>
                            </template>

                            <template v-else>
                                <span class="transcript-vvod-code">{{ lang.value }}</span>
                                <span class="transcript-vvod-label">{{ $vt(lang.label) }}</span>

                                <span class="transcript-vvod-info" v-if="isLang(lang, uploadingLangId)">
                                    <f-loader-circular size="16px"></f-loader-circular>{{ $vt('Importing...') }}
                                </span>
                                <span class="transcript-vvod-info drop" v-else-if="isLang(lang, dragLangId)">
                                    <f-icon icon="fa6-file-import"></f-icon>{{ $vt('Drop the .vtt file to replace these subtitles') }}
                                </span>
                                <span class="transcript-vvod-info" v-else-if="lang.count > 0">
                                    {{ $vt('{0} subtitles', [lang.count]) }}
                                </span>
                                <span class="transcript-vvod-info empty" v-else>
                                    <span class="transcript-vvod-dot"></span>{{ $vt('Empty') }}
                                </span>

                                <span class="transcript-vvod-actions">
                                    <f-button v-if="lang.count > 0 && exportUrl(lang)" type="tertiary" class="transcript-vvod-icon"
                                        :href="exportUrl(lang)" :title="$vt('Download')">
                                        <f-icon icon="fa6-download"></f-icon>
                                    </f-button>
                                    <f-button type="tertiary" class="transcript-vvod-icon" :disabled="!!uploadingLangId"
                                        :title="lang.count > 0 ? $vt('Replace') : $vt('Import a .vtt file')" @click="openReplace(lang)">
                                        <f-icon icon="fa6-upload"></f-icon>
                                    </f-button>
                                    <f-button type="tertiary negative" class="transcript-vvod-icon" :disabled="pending"
                                        :title="$vt('Delete')" @click="langToDelete = lang">
                                        <f-icon icon="fa6-trash"></f-icon>
                                    </f-button>
                                </span>
                            </template>

                        </div>

                    </div>

                    <div class="transcript-vvod-add">
                        <template v-if="showAdd">
                            <f-select ref="addSelect" class="transcript-vvod-add-select" filter-enabled :options="langOptions"
                                :selected.sync="selectedLang">
                                <template v-slot:label>{{ $vt('Add a language') }}</template>
                            </f-select>
                            <f-button type="tertiary" class="transcript-vvod-icon" :title="$vt('Cancel')" @click="cancelAdd()">
                                <f-icon icon="fa6-xmark"></f-icon>
                            </f-button>
                        </template>
                        <f-button v-else type="tertiary" :pending="pending" :disabled="!langOptions.length" @click="openAdd()">
                            <f-icon icon="fa6-plus"></f-icon>{{ $vt('Add a language') }}
                        </f-button>
                    </div>

                </template>

                <div class="transcript-vvod-hint">
                    <f-icon icon="fa6-circle-info"></f-icon>
                    {{ $vt('WebVTT (.vtt) files only. Other formats such as SRT are not supported.') }}
                </div>

            </template>

        </f-modal-base>

        <f-button v-if="component.has_subtitles || component.can_create_session" class="vvod-button" type="tertiary" @click="showModal = true"><f-icon icon="fa6-font"></f-icon>{{
            $vt('Subtitles') }}</f-button>
    </div>
</template>

<style lang="scss">
.transcript-vvod {
    .f-dialog {
        width: 800px;
    }

    .transcript-vvod-file {
        position: absolute;
        width: 0;
        height: 0;
        opacity: 0;
        overflow: hidden;
        z-index: -1;
    }

    .transcript-vvod-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 40px 10px;
        font-size: 14px;
        color: var(--white-a60);

        .transcript-vvod-state-icon {
            font-size: 40px;
            color: var(--white-a25);
        }
    }

    .transcript-vvod-langs {
        max-height: 50vh;
        overflow-y: auto;
    }

    .transcript-vvod-lang {
        position: relative;
        display: flex;
        align-items: center;
        gap: 12px;
        min-height: 60px;
        padding: 0 10px;
        border: 1px solid transparent;
        border-bottom-color: var(--white-a10);
        border-radius: 6px;
        transition: background-color 0.15s;

        &:hover {
            background-color: var(--white-a5);
        }

        &.dragover {
            border: 1px dashed var(--blue);
            background-color: var(--interactiveBlueHover);
        }

        &.confirm {
            background-color: var(--statusNegative-a10);
        }
    }

    .transcript-vvod-code {
        min-width: 56px;
        padding: 4px 8px;
        border-radius: 24px;
        background-color: var(--white-a10);
        color: var(--white-a75);
        font-size: 11px;
        font-weight: 700;
        text-align: center;
        text-transform: uppercase;
    }

    .transcript-vvod-label {
        font-size: 14px;
        font-weight: 600;
    }

    .transcript-vvod-info {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: auto;
        font-size: 12px;
        color: var(--white-a50);

        &.drop {
            color: var(--blue);
        }

        &.empty {
            color: var(--white-a50);
        }
    }

    .transcript-vvod-dot {
        width: 8px;
        height: 8px;
        border-radius: 4px;
        background-color: var(--white-a25);
    }

    .transcript-vvod-confirm {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: var(--statusNegative);
    }

    .transcript-vvod-actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .transcript-vvod-info + .transcript-vvod-actions {
        margin-left: 8px;
    }

    .f-button.transcript-vvod-icon {
        width: 40px;
        height: 40px;
        padding: 0;
    }

    .transcript-vvod-add {
        display: flex;
        align-items: flex-end;
        gap: 10px;
        padding: 10px 0;

        .transcript-vvod-add-select {
            flex: 1;
        }
    }

    .transcript-vvod-hint {
        display: flex;
        align-items: center;
        gap: 8px;
        padding-top: 10px;
        font-size: 12px;
        color: var(--white-a50);
    }
}
</style>
